/**
 * Tail stations — T2 step 3 (ADR-031 §3.3, D4; the station prior ruled
 * 2026-09-22): **walk the tail's own tube root → tip and cut it into bones by
 * the biological profile.**
 *
 * ONE responsibility: given the tail grid, the root and the tip, return the
 * joint positions of an N-bone chain and the evidence behind them. It walks
 * with `medialPath` (the hand arc's instrument — the route stays inside the
 * flesh and every station is re-seated on its cross-section centroid), and it
 * decides only one thing: WHERE ALONG the walk each joint sits.
 *
 * THE PRIOR — short → LONG → taper (memory `tail-anatomy-priors`, ADR-031 §2).
 * Caudal vertebrae are short at the sacrum, longest in the proximal third,
 * then taper to the tip. Segment k of N sits at t = (k − ½)/N along the tail
 * and gets weight
 *
 *     w(t) = first + (1 − first)·t/peak                 t ≤ peak   (rising)
 *     w(t) = 1 − (1 − tip)·(t − peak)/(1 − peak)        t > peak   (tapering)
 *
 * normalised so the N lengths sum to the walked length. `peak` is bounded to
 * the proximal third, so the longest bone is always there and every bone past
 * it is no longer than the one before (monotone taper) — both are asserted.
 * A prior, not a law: the knobs are declared constants with provenance, and
 * a Document may one day declare them per tail (§8).
 *
 * THE COUNT IS THE PERSON'S (D4). The default is `clamp(round(length /
 * meanRadius), 3, 16)` — a tail as many bones long as it is radii long,
 * inside the sixteen BBSA slots — and a declared count replaces it; more
 * bones = finer curvature, never stiffer. A count outside [3, 16] refuses by
 * name, naming the zone's kind and the band (§8).
 *
 * Everything is VOXEL-INDEX space. Depends on: hands/medial-path,
 * hands/voxel-grid. Depended on by: tails/place-tail.
 */
import { medialPath, type MedialPath } from '../hands/medial-path.js';
import type { VolumeGrid, VoxelPoint } from '../hands/voxel-grid.js';
import { TailRefusal } from './isolate-zone.js';

/** The band the sixteen BBSA `Tail1..Tail16` slots allow (ADR-031 §8, D4 ruled 2026-09-22). */
export const TAIL_COUNT_MIN = 3;
export const TAIL_COUNT_MAX = 16;

export interface TailPrior {
  /** First segment's weight relative to the peak (0, 1]. */
  readonly first: number;
  /** Where along the tail the longest segment sits, as a fraction in (0, ⅓]. */
  readonly peak: number;
  /** Last segment's weight relative to the peak (0, 1]. */
  readonly tip: number;
}

/**
 * The declared default prior. Provenance: memory `tail-anatomy-priors`
 * (caudal series: short sacral transition, longest vertebrae in the proximal
 * third, steady distal taper), ruled as the station prior by Allen 2026-09-22.
 * The numbers are a shape, not a measurement of any one species.
 */
export const TAIL_PRIOR: TailPrior = { first: 0.6, peak: 0.25, tip: 0.35 };

export interface TailCount {
  readonly value: number;
  readonly provenance: 'declared' | 'default';
}

export interface TailStations {
  /** count + 1 joints, root → tip, voxel space: joint k starts bone k+1; the last is the tip. */
  readonly joints: readonly VoxelPoint[];
  /** Segment lengths along the walk, voxels, root → tip (sum = walked length). */
  readonly segmentLengths: readonly number[];
  /** The cross-section radius at each joint (voxels) — the taper evidence. */
  readonly radii: readonly number[];
  readonly count: TailCount;
  /** What the default rule would have chosen — shown beside a declared count. */
  readonly defaultCount: number;
  /** Walked (medial) length root → tip, voxels. */
  readonly length: number;
  /** Mean cross-section radius over the walk, voxels. */
  readonly meanRadius: number;
  /** The walk itself, for the display and the probes. */
  readonly path: MedialPath;
}

function assertPrior(prior: TailPrior): void {
  const bad: string[] = [];
  if (!(prior.first > 0 && prior.first <= 1)) bad.push(`first ${prior.first} outside (0, 1]`);
  if (!(prior.tip > 0 && prior.tip <= 1)) bad.push(`tip ${prior.tip} outside (0, 1]`);
  if (!(prior.peak > 0 && prior.peak <= 1 / 3)) bad.push(`peak ${prior.peak} outside (0, ⅓] — the longest bone belongs to the proximal third`);
  if (bad.length > 0) throw new TailRefusal(`tail prior refused: ${bad.join('; ')}`);
}

/** The prior's weight at fraction t of the tail (see the module header). */
export function tailPriorWeight(t: number, prior: TailPrior = TAIL_PRIOR): number {
  return t <= prior.peak
    ? prior.first + ((1 - prior.first) * t) / prior.peak
    : 1 - ((1 - prior.tip) * (t - prior.peak)) / (1 - prior.peak);
}

/** Segment fractions of the whole length for an N-bone tail, summing to 1 — the closed form the probes check. */
export function tailSegmentFractions(count: number, prior: TailPrior = TAIL_PRIOR): number[] {
  assertPrior(prior);
  const w = Array.from({ length: count }, (_, k) => tailPriorWeight((k + 0.5) / count, prior));
  const total = w.reduce((a, b) => a + b, 0);
  return w.map((x) => x / total);
}

/** A segment is never shorter than this many of the tail's own mean radii… */
export const TAIL_SEGMENT_RADII = 1.2;
/** …nor than this fraction of the body's height (thin tails: the tigers' rope, the monkey's whip) — */
export const TAIL_SEGMENT_HEIGHT = 0.08;
/** — unless the tail is shorter than this many such segments (a short tail keeps a few bones: Amelie's puff). */
export const TAIL_SHORT_SEGMENTS = 5;
/**
 * A PLATE (a flat paddle) bends as a few long leaves, not a rope: its band.
 * Measured on Bonnie's paddle (#190, passive spring, walk): 8 / 11 / 15 bones
 * jitter 0.56 / 0.92 / 1.26 cm at the tip — the band tops out at 8.
 */
export const PLATE_COUNT_MIN = 6;
export const PLATE_COUNT_MAX = 8;

/**
 * The default count, from the tail's own geometry — calibrated on what Allen
 * chose by hand (2026-09-24): Amelie's short rabbit puff defaulted to 9, he
 * chose ~5; Magenta's 62 cm monkey whip defaulted to 16, "fewer bones worked
 * better", he chose 7; Bonnie's paddle got 15. A segment is no shorter than
 * {@link TAIL_SEGMENT_RADII} × the mean radius, nor than `minSegment` (the
 * caller's {@link TAIL_SEGMENT_HEIGHT} of the body height, same units as
 * `length`) — the floor easing for a tail shorter than
 * {@link TAIL_SHORT_SEGMENTS} such segments — clamped to the chain's [3, 16],
 * or [6, 8] for a `plate`. The person's count overrides.
 */
export function defaultTailCount(length: number, meanRadius: number, minSegment = 0, plate = false): number {
  const [lo, hi] = plate ? [PLATE_COUNT_MIN, PLATE_COUNT_MAX] : [TAIL_COUNT_MIN, TAIL_COUNT_MAX];
  if (!(length > 0) || !(meanRadius > 0)) return lo;
  const segment = Math.max(TAIL_SEGMENT_RADII * meanRadius, Math.min(minSegment, length / TAIL_SHORT_SEGMENTS));
  return Math.min(hi, Math.max(lo, Math.round(length / segment)));
}

/** A STUB tail's band: one or two bones — a springy single leaf, not a chain that curves. */
export const STUB_COUNT_MIN = 1;
export const STUB_COUNT_MAX = 2;

/** A stub's default count: one bone, two when it is more than one and a half radii long. */
export function defaultStubCount(length: number, meanRadius: number): number {
  return meanRadius > 0 && length / meanRadius > 1.5 ? STUB_COUNT_MAX : STUB_COUNT_MIN;
}

/** Refuse a count outside the band, naming the zone's kind — the §8 sentence. */
export function assertTailCount(count: number, zoneName: string, zoneKind: string, stub = false): void {
  if (stub) {
    if (!Number.isInteger(count) || count < STUB_COUNT_MIN || count > STUB_COUNT_MAX) {
      throw new TailRefusal(`zone "${zoneName}" (${zoneKind}): ${count} bones is outside the stub band [${STUB_COUNT_MIN}, ${STUB_COUNT_MAX}] — a stub tail is one springy leaf.`);
    }
    return;
  }
  if (!Number.isInteger(count) || count < TAIL_COUNT_MIN || count > TAIL_COUNT_MAX) {
    throw new TailRefusal(
      `zone "${zoneName}" (${zoneKind}): ${count} bones is outside the tail band [${TAIL_COUNT_MIN}, ${TAIL_COUNT_MAX}] — ` +
        `the chain has sixteen slots and needs three to curve.`,
    );
  }
}

export interface PlaceTailStationsOptions {
  readonly zoneName: string;
  /** The zone's kind for refusals (`tube`, or what the person said). */
  readonly zoneKind: string;
  /** A declared count; absent = the default rule. */
  readonly count?: number;
  readonly prior?: TailPrior;
  /** A STUB tail: one or two bones (the stub band), not the chain's [3, 16]. */
  readonly stub?: boolean;
  /** The default's shortest segment, VOXELS (the body-height floor); 0 = the radii rule alone. */
  readonly minSegment?: number;
  /** A PLATE (a flat paddle): the default lands in [6, 8]. */
  readonly plate?: boolean;
  /** A walk already made (a grouped fan's mass spine, bundle-path) — used instead of the medial walk. */
  readonly path?: MedialPath;
}

/** Walk root → tip and place the chain's joints by the prior. */
export function placeTailStations(
  grid: VolumeGrid,
  root: VoxelPoint,
  tip: VoxelPoint,
  options: PlaceTailStationsOptions,
): TailStations {
  const prior = options.prior ?? TAIL_PRIOR;
  assertPrior(prior);
  // The cross-section reach must see a thick tail's centre: a tail is fatter
  // in voxels than a finger (the default 6 was sized for digits).
  const path = options.path ?? medialPath(grid, root, tip, { crossSectionRadius: 12 });
  if (path === null || path.length <= 0) {
    throw new TailRefusal(`zone "${options.zoneName}": the tip cannot be reached from the root through the flesh — the zone is broken in two.`);
  }
  const radii = path.stations.map((s) => s.radius);
  const meanRadius = radii.reduce((a, b) => a + b, 0) / radii.length;
  const stub = options.stub === true;
  const defaultCount = stub
    ? defaultStubCount(path.length, meanRadius)
    : defaultTailCount(path.length, meanRadius, options.minSegment ?? 0, options.plate === true);
  const count: TailCount =
    options.count !== undefined ? { value: options.count, provenance: 'declared' } : { value: defaultCount, provenance: 'default' };
  assertTailCount(count.value, options.zoneName, options.zoneKind, stub);

  const fractions = tailSegmentFractions(count.value, prior);
  const joints: VoxelPoint[] = [path.at(0)];
  let acc = 0;
  for (const f of fractions) {
    acc += f;
    joints.push(path.at(Math.min(1, acc)));
  }
  // The radius at a joint: the walk's own station nearest that arclength.
  const radiusAt = (fraction: number): number => {
    const target = fraction * path.length;
    let best = path.stations[0]!;
    for (const s of path.stations) if (Math.abs(s.arclength - target) < Math.abs(best.arclength - target)) best = s;
    return best.radius;
  };
  let cum = 0;
  const jointRadii = [radiusAt(0), ...fractions.map((f) => radiusAt(Math.min(1, (cum += f))))];
  return {
    joints,
    segmentLengths: fractions.map((f) => f * path.length),
    radii: jointRadii,
    count,
    defaultCount,
    length: path.length,
    meanRadius,
    path,
  };
}
